// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Metric from './Metric';
import DonutChart from './DoughnutChart';
import api from '../../api';
import helpers from './helpers';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';

import '../../styles/investments.css';
import '../../styles/investments-summary.css';
import '../../styles/investments-filters.css';
import '../../styles/paginator.css';
import '../../styles/investments-status.css';
import '../../styles/statementCard.css';

import Listable from './Listable';
import Filter from './Filter';
import Paginator from '../Paginator';
import Dropdown from './Dropdown';

export default class Status extends React.Component {

    abortController = new AbortController();

    state = {
        accounts: [],
        stats: undefined,
        missingStatementCount: 0,
        accountsOnTrack: 0,
        accountsTodo: 0, 
        accountsMissing: 0, 
        currentPage: 0, 
        pageSize: 20, 
        show: 'all',
        filteredAccounts: [], 
        loading: false, 
        pinnedAccounts: [],  
    }

    componentDidMount = () => {
        this.fetchAccountStatus(); 
        this.paginator = React.createRef(); 
        this.props.setTitle("Trust Accounting Status")
        // Do we have a query param for what to show? set it... 
        const show = new URLSearchParams(window.location.search).get('show'); 
        if(show) {
            this.setState({show});
        } 
    }

    componentDidUpdate = (prev) => {
        // Did the search query change? 
        if(prev.planYear !== this.props.planYear) {
            this.fetchAccountStatus(); 
        }

        if (JSON.stringify(prev.filters) !== JSON.stringify(this.props.filters) || prev.query !== this.props.query) {
            this.filterAccounts();
        }

        // Did the query change? 
        if(prev.query !== this.props.query) {
            this.setState({currentPage: 0})
        }   
    }

    fetchAccountStatus = async () => {

        this.abortController.abort();
        this.abortController = new AbortController();

        // Reset everything to default... (Shows loading)
        this.setState({accounts: [], filteredAccounts: [], stats: undefined, loading: true, pinnedAccounts: []});

        const res = await api.post("/tpa/status/accounts", {
            planYear: this.props.planYear,
        }, false, {signal: this.abortController.signal});
        
        if(!res.success && !res.cancelled){
            return toast.error("Failed to load account status")
        }

        if(res.cancelled)
            return;  

        const accounts = res.accounts.map((account) => {
            // Combine statements with the same document into one. Keep the highest month. 
            let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            let startMmIndex = (account.endMm??12) % 12;
            months = [...months.slice(startMmIndex), ...months.slice(0, startMmIndex)];

            const statements = Object.values(Object.groupBy(account.statements, (s) => (s.document? s.document: Math.random()))).map((statementGroup) => {

                // Build the full statement... 
                // find the last month in the statementGroup... 
                const month = statementGroup.reduce((acc, statement) => {
                    const monthIndex = months.indexOf(statement.month);
                    return monthIndex > acc ? monthIndex : acc;
                }
                , 0);

                const statement = {
                    ...statementGroup[0],
                    span: statementGroup.length,
                    coveredMonths: statementGroup.map((s) => s.month),
                    month: months[month],
                    index: month, 
                }
                return statement;
            }).sort((a, b) => a.index - b.index);

            // Go through each statement (Starting at the second) and see if it is the same as the previous...
            for(let i = 1; i < statements.length; i++) {
                const prev = statements[i - 1];
                const current = statements[i]; 
                // Do we care about the previous statement? 
                if(prev.status === 'na' || prev.status === 'missing')
                    continue;
                // Do we care about the current statement?
                if(current.status === 'na' || current.status === 'missing')
                    continue;

                // Does the beginning of this statement match the ending of the previous? 
                prev.misMatch = prev.endingBalance !== current.beginningBalance && prev.balanceAccrued !== current.beginningBalance; 
                // If we got to this point. the account cannot be complete. Change the status as missing... 
                // Get the accounts from state...
                if(prev.misMatch && (account.status === 'complete' || account.status === 'onTrack'))
                    account.status = 'todo';
            }
            account.statements = statements; 
            return account; 
        });

        // // Accounts also will not change... we can have a state set to filter what is shown, and a class variable to hold all accounts... 
        this.setState({accounts, stats: res.stats, loading: false}, () => {
            this.filterAccounts(); 
        });
    }

    Capitalize = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    exportPlanToExcel = async () => {
        this.props.loading(); 
        const workbook = new ExcelJS.Workbook();
        const response = await fetch('/templates/reportStatus.xlsx');
        const arrayBuffer = await response.arrayBuffer();

        await workbook.xlsx.load(arrayBuffer);
        let worksheet = workbook.getWorksheet(1); // Get the first sheet

        let cell = worksheet.getCell('G1');
        cell.value = this.props.planYear;

        let startingRow = 5; // The row index where you want to start

        // Get all of the accounts we want to export... 
        const filteredAccounts = [...this.state.filteredAccounts];
        const pinnedAccounts = [...this.state.pinnedAccounts]; 
        // Combine the arrays, and remove duplicates... 
        const accounts = [...filteredAccounts, ...pinnedAccounts].filter((account, index, self) => {
            return index === self.findIndex((a) => a.accountId === account.accountId);
        });

        accounts.forEach((account, index) => {
            // Get the row at the starting position
            let row = worksheet.getRow(startingRow + index);

            let statusCount = account.status === "missing" ? account.statements.filter(m => m.status === "missing").length : 0;

            // One cell per fiscal month of the plan year. Statuses come from /tpa/status/accounts, which applies the
            // shared rule (identical in stax-ui and stax-api, do not deviate): a month is expected when its
            // month-end is before the first day of the current month. No calendar-year check.
            const monthCells = helpers.statusExportCells(account, this.props.planYear);
            const accountStatus = monthCells.map((m) => m.status);

            // Set the values for each cell in the row
            row.values = [
                account.internalPlanId, 
                account.planName, 
                `${account.endMm}-${account.endDd}`, 
                account.account, 
                account.payee, 
                account.provider,
                statusCount ? `${statusCount} ${this.Capitalize(account.status)}` : this.Capitalize(account.status),
                ...accountStatus,
            ];

            // Loop through each cell in the row to set styles
            row.eachCell((cell, colNumber) => {
                // Account number
                if (colNumber === 4) {
                    cell.font = { underline: true, color: { argb: '0070C0' }};  
                    cell.value = {
                        text: account.account, 
                        hyperlink: `https://my.stax.ai/field/account/${cell.value}`, 
                        tooltip: 'Click to open' 
                    };
                } 

                if (colNumber === 7) { 
                    if (cell.value === 'Complete') {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: '00B050' }, // Green fill color 
                        };
                    }
                    else if (cell.value === 'OnTrack') {
                        cell.value = 'On Track';
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: '--palette-color-8' }, 
                        };
                    }
                    else if (cell.value === 'Todo') {
                        cell.value = 'To Do';
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFC000' }, // Dusty fill color 
                        };
                    }
                    else if (cell.value.includes('Missing')) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'C00000' }, // Black fill color 
                        };
                    }
                    // bold font
                    cell.font = {
                        bold: true,
                        color: { argb: 'FFFFFFFF' } // White font color
                    };
                }

                if (colNumber > 7) {
                    const monthCell = monthCells[colNumber - 8];

                    if (cell.value === 'complete' || cell.value === 'onTrack') {
                        cell.font = { underline: true, color: { argb: '0070C0' } };
                        cell.value = {
                            text: monthCell.label, 
                            hyperlink: `https://my.stax.ai/document/${monthCell.document}`, 
                            tooltip: 'Click to open' 
                        };
                    }

                    else if (cell.value === 'missing') {
                        cell.font = { color: { argb: 'FFFFFFFF' } };
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'C00000' }, 
                        };
                        cell.value = monthCell.label;
                    }

                    else if (cell.value === 'todo') {
                        cell.font = { underline: true, color: { argb: '0070C0' } };
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFC000' }, 
                        };
                        cell.value = {
                            text: monthCell.label,
                            hyperlink: `https://my.stax.ai/document/${monthCell.document}`, 
                        }
                    
                    }

                    else if (cell.value === 'na') {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'BABABA' }, // Red fill color 
                        };
                        cell.value = '';
                    }
                }
            });

            // Commit the row to the worksheet
            row.commit();
        });

        worksheet = workbook.getWorksheet(2);

        cell = worksheet.getCell('E1');
        cell.value = this.props.planYear;

        const groupedByPlanId = accounts.reduce((plan, item) => {
            if (!plan[item.internalPlanId]) {
              plan[item.internalPlanId] = { "status": [], "name": item.planName, "pye": `${item.endMm}-${item.endDd}` };
            }
            plan[item.internalPlanId]["status"].push(item.status);

            return plan;
        }, {});

        Object.entries(groupedByPlanId).forEach(([internalPlanId, plan], index) => {
            const planStatus = plan.status.includes("missing") ? "Missing" : plan.status.includes("todo") ? "To Do" : plan.status.includes("onTrack") ? "On Track" : "Complete";
            let row = worksheet.getRow(startingRow + index);

            row.values = [
                planStatus, 
                internalPlanId, 
                plan.name,
                plan.pye
            ];

            let firstCell = row.getCell(1);
            firstCell.font = { color: { argb: 'FFFFFFFF' }, bold: true};

            if (planStatus === "Complete") {
                firstCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '00B050' },  
                };
            }
            else if (planStatus === "On Track") {
                firstCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '--palette-color-8' }, 
                };
            }

            else if (planStatus === "To Do") {
                firstCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFC000' }, 
                };
            }

            else if (planStatus === "Missing") {
                firstCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'C00000' }, 
                };
            }

            row.commit();

        });

        // download the file
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'TPA Report Status.xlsx';
            link.click();
        });

        this.props.loaded(); 
    }
    
    filterAccounts = () => {
        let filteredAccounts = this.state.accounts?.filter((account) => {
            if(this.state.show === 'all') {
                return true; 
            }
            else if(this.state.show === 'incomplete') {
                return account?.status === 'missing';
            }
            else if(this.state.show === 'missingAll') {
                return account.statements.every((s) => s.status === 'missing');
            }
            else if(this.state.show === 'on-track') {
                return account?.status === 'onTrack';
            }
            else if(this.state.show === 'complete') {
                return account?.status === 'complete';
            }
            else if(this.state.show === 'review') {
                return account.statements.some((s) => s.status === 'todo');
            }
            else if(this.state.show === 'pinned') {
                return false;
            }
            else if(this.state.show === 'reviewed') {
                return account?.viewed?.[this.props.planYear];
            }
            return true; 
        });

        // Do we have a search query? 
        if(this.props.query) {
            const query = this.props.query.toLowerCase();
            filteredAccounts = filteredAccounts.filter((account) => {
                // Does any key value contain the query? 
                return Object.values(account).some((value) => {
                    if(typeof value === 'string') {
                        return value.toLowerCase().includes(query);
                    }
                    else if(Array.isArray(value)) {
                        return value.some((v) => v.toString().toLowerCase().includes(query));
                    }
                    return false;
                });
            });
        }
        // Do we have any filters?
        const filters = {...this.props.filters};

        if (filters.pye.from.endDd !== 1 || filters.pye.from.endMm !== 1 || filters.pye.to.endDd !== 31 || filters.pye.to.endMm !== 12) {
            filteredAccounts = filteredAccounts.reduce((acc, a) => {
                if(Object.entries(filters).every(([key, value]) => {

                    if(key !== 'pye')
                        return true; 

                    const {from, to} = value;   
                    const fromDate = new Date(this.props.planYear, from.endMm-1, from.endDd)
                    const toDate = new Date(this.props.planYear, to.endMm-1, to.endDd); 
                    const date = new Date(this.props.planYear,a.endMm-1, a.endDd); 

                    return date >= fromDate && date <= toDate; 
                }))
 
                    return [...acc, a];
                return acc 
            }, []);
        }

        if(Object.values(filters).some((f) => f !== null && f.length > 0)) {
            filteredAccounts = filteredAccounts.filter((account) => {
                return Object.entries(filters).every(([key, value]) => {
                    if(value === null || value.length === 0)
                        return true; 
                    if(key === 'provider') {
                        return value.includes(account.provider);
                    }
                    else if(key === 'status') {
                        return value.includes(account.status);
                    }
                    else if(key === 'payee') {
                        return value.includes(account.payee);
                    }
                    else if(key === 'planName') {
                        return value.includes(account.planName);
                    }
                    else if(key === 'internalPlanId') {
                        return value.includes(account.internalPlanId);
                    }
                    else if(key === 'account') {
                        return value.includes(account.account);
                    }
                    return true; 
                });
            });
        }

        this.setState({filteredAccounts});
    }

    render = () => {
        return (<>
            <div style={{ clear: 'both', height: 20 }} />
            <div
                className='investments-status-stat-container'
            >
                <div className='investment-status-chart-container'>
                    <DonutChart 
                        title={""}
                        labels={["Complete", "On-Track", "Missing & To-Do", "To-Do", "Missing Statements"]}
                        data={this.state.stats === undefined? undefined: [
                            this.state.stats?.numAccountsByStatus?.complete??0,
                            this.state.stats?.numAccountsByStatus?.onTrack??0,
                            this.state.stats?.numAccountsByStatus?.missing_todo??0, 
                            this.state.stats?.numAccountsByStatus?.todo??0,
                            this.state.stats?.numAccountsByStatus?.missing??0,
                        ]}
                        user={this.props.user}
                        colors={[
                            '#00b770',
                            '#24f1be',
                            '#fcd12a',
                            'rgb(255, 129, 59)',
                            '#ff4352',
                        ]}
                    />
                </div>

                 <Metric
                label="STATEMENTS MISSING"
                value={this.state.stats? (this.state.stats.totalMissingStatements??0).toLocaleString(): undefined}
                />
                 <Metric
                label="INCOMPLETE ACCOUNTS"
                value={this.state.stats? (this.state.stats.numAccountsByStatus.missing??0).toLocaleString(): undefined}
                />
                <Metric
                    label="TO-DO ACCOUNTS"
                    value={this.state.stats? (this.state.stats.totalTodoAccounts??0).toLocaleString(): undefined}
                />
            </div>
            <div style={{marginTop: 40, paddingLeft: 20}}>
                <p>
                Below is a list of all accounts with missing statements whose expected period end date is prior to 30 days ago. Click on the months to open the statements. Red statements are missing and need to be uploaded. Statements with an abnormal number of pages in comparison to others from this provider are underlined and may contain multiple statements that need to be split.
                </p>
            </div>
            <div style={{
                marginLeft: 20,
                marginTop: 60,
                position: 'relative',
            }}>

                <div
                    style={{
                        marginBottom: 30,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <h3>Status by Investment Accounts</h3>
                    <div
                        style={{marginLeft: 20}}
                    >
                        <Dropdown 
                            value={this.state.show}
                            options={[
                                {
                                    "label": "All Accounts",
                                    "value": "all"
                                },
                                {
                                    "label": "Accounts Missing Statements",
                                    "value": "incomplete"
                                },
                                {
                                    "label": "Accounts Missing All Statements",
                                    "value": "missingAll"
                                },
                                {
                                    "label": "To-Do Accounts",
                                    "value": "review"
                                },
                                {
                                    "label": "On-Track Accounts",
                                    "value": "on-track"
                                },
                                {
                                    "label": "Complete Accounts",
                                    "value": "complete"
                                },
                                {
                                    "label": "Reviewed Accounts",
                                    "value": "reviewed",
                                },
                                {
                                    "label": "Pinned Accounts",
                                    "value": "pinned"
                                },

                            ]}
                            onChange={(e) => {
                                this.setState({show: e, currentPage: 0}, () => {
                                    this.filterAccounts();
                                }); 
                                // Set a query param for show... 
                                const params = new URLSearchParams(window.location.search); 
                                params.set('show', e);
                                window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
                            }}
                        />
                    </div>
                   
                    <div
                        style={{marginLeft: 'auto', marginRight: 60}}
                    >
                        <Paginator 
                            items={this.state.filteredAccounts}
                            currentPage={this.state.currentPage} 
                            pageSize={this.state.pageSize}
                            setPage={page => this.setState({currentPage: page})}
                            setPageLimit={limit => this.setState({pageSize: limit, currentPage: 0}, () => { this.filterAccounts()})} 
                        /> 
                    </div>
                    { this.state.accounts.length ?
                        <div
                        >
                            <Filter 
                                onClick={() => {
                                    this.props.modal('filter-status-accounts', {accounts: this.state.accounts, filterAccounts: this.filterAccounts})
                                }}
                                label="Filter Accounts"
                                filterCount={Object.entries({...this.props.filters}).filter(f => {
                                        const [key, value] = f;
        
                                        // Check if f is not null
                                        if (value !== null) {
                                            // Check if f is an array and has a length greater than 0
                                            if (Array.isArray(value) && value.length > 0) {
                                                return true;
                                            }
                                            // Check if f is an object (but not an array, as arrays are also objects)
                                            else if (typeof value === 'object' && !Array.isArray(value)) {
                                                // if the key is pye and the date is 12/31, don't count it
                                                if ((key === 'pye' || key === 'settleDate' ) && value.from.endDd === 1 && value.from.endMm === 1 && value.to.endDd === 31 && value.to.endMm === 12) {
                                                    return false;
                                                } 
                                                // Check if the object has at least one property set
                                                return Object.values(value).some(val => val !== null && val !== undefined && val !== '');
                                            }
                                        }
                                        return false;
                                    }).length
                                } 
                                onClear={async() => {
                                    await this.props.clearFilters();
                                }}
                            /> 
                        </div>
                        : ""
                    }
                        
                    <div 
                        className='a investments-plan-summary-export-excel' 
                        style={{
                            backgroundImage: "url('https://static.stax.ai/images/icons/action-export-excel.png')",
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'left',
                            position: 'absolute',
                            paddingLeft: 20,
                            top: 65,
                            right: 0, 
                            zIndex: 99,
                            margin: 0,
                        }}
                        onClick={() => this.exportPlanToExcel()}
                    >
                        Export to Excel
                    </div>
                </div>

                <Listable 
                    columns={[
                        {
                            header: 'Plan ID',
                            width: 60, 
                            align: 'left',
                            sortable: true,
                            prop: 'internalPlanId',
                            grow: false, 
                            render: (item) => {
                                return (
                                    <div
                                        className='listable-status-column'
                                        onClick={() => {
                                            window.open(`/investments/plan/${item.planId}?planYear=${this.props.planYear}`, '_blank');
                                        }}
                                        style={{cursor: 'pointer'}}
                                    >
                                        {item.internalPlanId}
                                    </div>
                                )
                            }
                        },
                        {
                            header: 'Plan Name',
                            width: 10, 
                            align: 'left',
                            sortable: true,
                            prop: 'planName',
                            render: (item) => {
                                return (
                                    <div
                                        className='listable-status-column'
                                        style={{cursor: 'pointer'}}
                                        onClick={() => {
                                            window.open(`/investments/plan/${item.planId}?planYear=${this.props.planYear}`, '_blank');
                                        }}
                                    >
                                        {item.planName}
                                    </div>
                                )
                            }
                        },
                        {
                            header: 'Provider',
                            width: 10, 
                            align: 'left',
                            sortable: true,
                            prop: 'provider',
                            render: (item) => {
                                return (
                                    <div className='listable-status-column'>
                                        {item.provider}
                                    </div>
                                )
                            }
                        },
                        {
                            header: 'Account',
                            width: 10, 
                            align: 'left',
                            sortable: true,
                            prop: 'account',
                            render: (item) => {
                                return (
                                    <div
                                        className='listable-status-column'
                                        onClick={() => {
                                            window.open(`/investments/plan/${item.planId}/account/${item.accountId}?planYear=${this.props.planYear}`, '_blank'); 
                                        }}
                                        style={{cursor: 'pointer'}}
                                    >
                                        {item.account}
                                    </div>
                                )
                            }
                        },
                        {
                            header: 'FBO/Payee',
                            width: 10, 
                            align: 'left',
                            sortable: true,
                            prop: 'payee',
                            render: (item) => {
                                return (
                                    <div className='listable-status-column'>
                                        {item.payee??""}
                                    </div>
                                )
                            }
                        },
                        {
                            header: 'Statements',
                            width: 610, 
                            align: 'left',
                            grow: false, 
                            render: (item) => {
                                if(Object.keys(item).length === 0)
                                    return; 
                                const card = ({month, status, document, span, pageCount, misMatch}) => {

                                    const baseWidth = 35 * span;  // Standard width 
                                    const widthOffset = 10 * (span - 1);
                                    const gapOffset = 6 * (span - 1); 

                                    const width = baseWidth + widthOffset + gapOffset; 

                                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                    // We have a 3 letter month, get the full month name from that. 
                                    const fullMonth = new Date(this.props.planYear, months.indexOf(month), 1).toLocaleString('default', { month: 'long' });

                                    return (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                        <div
                                            className={`card ${status} ${span > 1? 'span': ''}`}
                                            style={{
                                                minWidth: width,
                                                maxWidth: width,
                                                zIndex: 10
                                            }}
                                            title={`${pageCount} pages`}
                                            onClick={() => {
                                                if(document) {
                                                    window.open(`/document/${document}`, '_blank');
                                                } else {
                                                    const query = `"${item.account}" + ("${month}" | "${fullMonth}") + "${this.props.planYear}"`;
                                                    const params = new URLSearchParams(); 
                                                    params.set('from', 'root');
                                                    params.set('view', 'list');
                                                    params.set('format', 'doc');
                                                    window.open(`/search/${query}?${params.toString()}`, '_blank');
                                                }
                                            }}
                                        >
                                            {month.toUpperCase()}
                                        </div>
                                        
                                        <div 
                                            className='status-balance-mismatch'
                                            style={misMatch ? {} : {
                                                backgroundColor: 'transparent',
                                            }}
                                        />

                                        </div>
                                    )
                                };


                                return (
                                    <div style={{display: 'flex', gap: 0, overflow: 'hidden', marginLeft: 'auto'}}>
                                    {
                                        item.statements.map((statement, index) => {
                                            return card(statement);
                                        })
                                    }
                                    </div>
                                )
                            }
                        },
                        {
                            header: '',
                            width: 30, 
                            prop: 'reviewed',
                            grow: false, 
                            render: (item) => {
                                const reviewer = item?.viewed?.[this.props.planYear]; 
                                if(!reviewer)
                                    return;

                                let userName = (this.props.team?.users.find(user=>user._id === reviewer))?.name
                                return (
                                    <div 
                                        className="document-thumbnail-icon-assign"
                                        style={{
                                            position: 'static',
                                            marginTop: 5,
                                        }}
                                        title={`This account was reviewed by ${userName}`}
                                    >
                                        {userName ? userName[0] : "M"}
                                    </div>
                                );
                            }
                        }
                    ]}
                    page={this.state.currentPage}
                    pageSize={this.state.pageSize}
                    data={(() => {
                        if(!this.state.accounts.length)
                            return Array(this.state.pageSize).fill({}); 
                        return[
                            ...this.state.pinnedAccounts, 
                            ...this.state.filteredAccounts
                        ].map((account) => {
                            return {
                                ...account, 
                                _id: `${account.account}_${account.internalPlanId}`
                            }
                        })
                    })()}
                    contextMenuOptions={[
                        {
                            label: 'Copy value',
                            action: (item) => {
                                // Write the value to clipboard... 
                                navigator.clipboard.writeText(item.copyValue??"");
                                toast.success("Copied to clipboard!");
                            }
                        },
                        {
                            label: "Open in plan editor",
                            action: (item) => {
                                // Open a new windows to the plan editor... /settings/plan-editor 
                                window.open(`/settings/plan-editor?search=${item.account}`, '_blank');
                            }
                        },
                        {
                            label: "View plan summary",
                            action: (item) => {
                                window.open(`/investments/plan/${item.planId}?planYear=${this.props.planYear}`, '_blank'); 
                            }
                        },
                        {
                            label: "View account summary",
                            action: (item) => {
                                window.open(`/investments/plan/${item.planId}/account/${item.accountId}?planYear=${this.props.planYear}`, '_blank'); 
                            }
                        },
                        {
                            label: "Show account documents",
                            action: (item) => {
                                // Open a new windows to the plan editor... /settings/plan-editor 
                                window.open(`/field/account/${item.account}`, '_blank');
                            }
                        },
                        {
                            label: (item) => {
                                return item?.pinned || this.state.pinnedAccounts.find((a) => a.accountId === item?.accountId)? "Unpin account": "Pin account";
                            },
                            action: (item) => {
                                let pinnedAccounts = [...this.state.pinnedAccounts];
                                let unpinnedAccounts = [...this.state.filteredAccounts]; 
                                // Is this item already pinned?  
                                if(item.pinned || this.state.pinnedAccounts.find((a) => a.accountId === item.accountId)) {
                                    // Remove the item from the pinned accounts...
                                    pinnedAccounts = this.state.pinnedAccounts.filter((a) => a.accountId !== item.accountId);
                                    unpinnedAccounts = [...unpinnedAccounts, {...item, pinned: false}];
                                }else{
                                    pinnedAccounts = [...pinnedAccounts, {...item, pinned: true}];
                                    unpinnedAccounts = unpinnedAccounts.filter((a) => a.accountId !== item.accountId);
                                }


                                this.setState({pinnedAccounts, filteredAccounts: unpinnedAccounts}); 
                            }
                        },
                        {
                            label: 'Ignore account',
                            action: (item) => {
                                this.props.modal("confirm-caution", {
                                    message: <>
                                        If you ignore this account, it will not be considered for determining the status of the plan. Are you sure you want to ignore this account?
                                    </>,
                                    action: async() => {

                                        // Update the stats quickly... 
                                        const accountStatus = item.status; 
                                        const stats = {...this.state.stats};
                                        stats.numAccountsByStatus[accountStatus]--; 

                                        const numMissingStatements = item.statements.filter((s) => s.status === 'missing').length; 
                                        stats.totalMissingStatements -= numMissingStatements;
                                        
                                        // Check if the account had any todo statements... if so, decrement the total todo accounts... 
                                        if(item.statements.some((s) => s.status === 'todo')) { 
                                            stats.totalTodoAccounts--; 
                                        } 


                                        // Remove this account from what is being rendered... 
                                        const accounts = this.state.accounts.filter((a) => a.accountId !== item.accountId); 

                                        this.setState({accounts, stats}, () => {
                                            this.filterAccounts(); 
                                        });

                                        // Make a request to actually update the account, so on future loads it wont be considered...
                                        const res = await api.post('/tpa/account/update', {
                                            id: item.accountId,
                                            accountDiff: {
                                                accountType: 'ignored'
                                            }
                                        });
                                        if(!res.success)
                                            return toast.error("Failed to ignore the account")
                                    }
                                })
                            }
                        },                      
                    ]}
                    loading={this.state.loading}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: 20,
                        marginBottom: 20,
                    }}
                >
                <div
                    style={{
                        width: 'fit-content',
                        visibility: !this.state.loading? 'visible': 'hidden'
                    }}
                >
                    <Paginator 
                        items={this.state.filteredAccounts}
                        currentPage={this.state.currentPage} 
                        pageSize={this.state.pageSize}
                        setPage={page => this.setState({currentPage: page})}
                        setPageLimit={limit => this.setState({pageSize: limit, currentPage: 0}, () => { this.filterAccounts()})}    
                        bottom={true}
                    /> 
                </div>
                </div>
               

            </div>
        </>);
    }
}